import type { CanvasJSON, CanvasJSONLikeObject, FieldBinding } from '@doer/frontend-components/creativity-editor';
import type { RoutedFormData } from '../../../types/templates';
import { EFFECTS } from './effects';
import { buildPatches, type Patch } from './core/patches';
import { preMountAllJson } from './binders';

const EMPTY_DEFAULTS: RoutedFormData = {};

// Like Fabric's `walkObjects` but for plain JSON.
function walkJsonObjects<T extends Record<string, unknown>>(objects: T[], fn: (obj: T) => T): T[] {
  return objects.map(obj => {
    const transformed = fn(obj);
    if (Array.isArray(transformed.objects)) {
      return { ...transformed, objects: walkJsonObjects(transformed.objects as T[], fn) };
    }
    return transformed;
  });
}

function applyPatchesToJson(canvasJSON: CanvasJSON, patches: Map<string, Patch>): CanvasJSON {
  if (patches.size === 0) return canvasJSON;
  return {
    ...canvasJSON,
    objects: walkJsonObjects(canvasJSON.objects, obj => {
      // Opt-out from the "Sincronizar con otras resoluciones" toggle.
      if (obj.linkedAcrossResolutions === false) return obj;
      const patch = patches.get(obj.pieceId as string);
      if (!patch) return obj;
      return EFFECTS[patch.bindingType].applyToJson(obj, patch.value) ?? obj;
    })
  };
}

export function applyBindingsToJson(
  canvasJSON: CanvasJSON,
  formData: RoutedFormData,
  fieldBindings: FieldBinding[],
  resolutionId?: string,
  defaults: RoutedFormData = EMPTY_DEFAULTS
): CanvasJSON {
  const mountedObjects = preMountAllJson(
    canvasJSON.objects as CanvasJSONLikeObject[],
    fieldBindings,
    formData,
    resolutionId
  );
  const withMount: CanvasJSON = { ...canvasJSON, objects: mountedObjects as CanvasJSON['objects'] };
  const patches = buildPatches(fieldBindings, formData, defaults, resolutionId);
  return applyPatchesToJson(withMount, patches);
}
